import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import config from '../../config';
import { rbacUrl } from '../rbac/api';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';

const authHeaders = () => ({
  Authorization: `Bearer ${sessionStorage.getItem('token')}`,
  'Content-Type': 'application/json',
});

export default function RbacRoleManagement() {
  const [roles, setRoles] = useState([]);
  const [matrix, setMatrix] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [matrixError, setMatrixError] = useState('');

  const normalizeMatrix = (data) => {
    if (!Array.isArray(data)) return [];
    return data.map((mod) => ({
      moduleKey: mod.moduleKey ?? mod.ModuleKey,
      moduleName:
        mod.moduleName ?? mod.ModuleName ?? mod.moduleKey ?? mod.ModuleKey,
      permissions: (mod.permissions ?? mod.Permissions ?? []).map((p) => ({
        id: p.id ?? p.Id,
        code: p.code ?? p.Code,
        name: p.name ?? p.Name,
      })),
    }));
  };

  const load = useCallback(async () => {
    setLoading(true);
    setMatrixError('');
    try {
      const [rRes, mRes] = await Promise.all([
        fetch(rbacUrl('rbac/roles'), { headers: authHeaders() }),
        fetch(rbacUrl('rbac/permissions/matrix'), {
          headers: authHeaders(),
        }),
      ]);
      if (rRes.ok) setRoles(await rRes.json());
      if (mRes.ok) {
        const normalized = normalizeMatrix(await mRes.json());
        setMatrix(normalized);
        if (normalized.length === 0) {
          setMatrixError(
            'No permissions in database. Run SQL seed script (see message below), then refresh.',
          );
        }
      } else {
        setMatrix([]);
        setMatrixError(
          mRes.status === 403
            ? 'Access denied. Log in as Super Admin.'
            : `Could not load permission matrix (HTTP ${mRes.status}).`,
        );
      }
    } catch (e) {
      console.error(e);
      setMatrixError('Failed to load roles and permissions.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setName('');
    setDescription('');
    setSelectedIds(new Set());
    setOpen(true);
  };

  const openEdit = async (role) => {
    setEditing(role);
    setName(role.name);
    setDescription(role.description || '');
    try {
      const res = await fetch(rbacUrl(`rbac/roles/${role.id}`), {
        headers: authHeaders(),
      });
      if (res.ok) {
        const detail = await res.json();
        setSelectedIds(
          new Set(detail.permissionIds || detail.PermissionIds || []),
        );
      }
    } catch {
      setSelectedIds(new Set());
    }
    setOpen(true);
  };

  const togglePerm = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const save = async () => {
    if (!name.trim()) {
      toast.error('Role name required');
      return;
    }
    const body = {
      name: name.trim(),
      description,
      permissionIds: Array.from(selectedIds),
    };
    const url = editing
      ? rbacUrl(`rbac/roles/${editing.id}`)
      : rbacUrl('rbac/roles');
    const res = await fetch(url, {
      method: editing ? 'PUT' : 'POST',
      headers: authHeaders(),
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      toast.error('Save failed');
      return;
    }
    setOpen(false);
    load();
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant='h5' fontWeight={700}>
          Role & Permission Management
        </Typography>
        <Button
          variant='contained'
          startIcon={<AddIcon />}
          onClick={openCreate}>
          Create Role
        </Button>
      </Box>

      {matrixError && (
        <Paper
          sx={{ p: 2, mb: 2, bgcolor: '#fff7ed', border: '1px solid #fdba74' }}>
          <Typography variant='body2' color='error' sx={{ mb: 1 }}>
            {matrixError}
          </Typography>
          <Typography variant='caption' component='div'>
            In SQL Server Management Studio, run on your hotel database:
          </Typography>
          <Typography
            variant='caption'
            component='pre'
            sx={{
              mt: 1,
              p: 1,
              bgcolor: '#f1f5f9',
              borderRadius: 1,
              overflow: 'auto',
            }}>
            {`NatureFarm_HotelAPI/Scripts/Add_Rbac_Tables_AndSeed.sql\n-- or --\nNatureFarm_HotelAPI/Scripts/Enterprise_Rbac_Full.sql`}
          </Typography>
          <Button size='small' sx={{ mt: 1 }} onClick={load}>
            Refresh after running SQL
          </Button>
        </Paper>
      )}

      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Role</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align='right'>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.map((r) => (
              <TableRow key={r.id}>
                <TableCell>
                  {r.name}
                  {r.isSuperAdmin && (
                    <Typography
                      component='span'
                      variant='caption'
                      color='primary'
                      sx={{ ml: 1 }}>
                      Super Admin
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{r.description}</TableCell>
                <TableCell align='right'>
                  <Button
                    size='small'
                    startIcon={<EditIcon />}
                    onClick={() => openEdit(r)}>
                    Edit permissions
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth='md'
        fullWidth>
        <DialogTitle>{editing ? 'Edit Role' : 'Create Role'}</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label='Role name'
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            fullWidth
            label='Description'
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Typography variant='subtitle2' sx={{ mb: 1 }}>
            Permissions (module matrix)
          </Typography>
          {matrix.length === 0 && (
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              No permissions loaded. Run the RBAC seed SQL on your database,
              click &quot;Refresh after running SQL&quot; on the main page, then
              open this dialog again.
            </Typography>
          )}
          {matrix.map((mod) => (
            <Accordion
              key={mod.moduleKey}
              disableGutters
              elevation={0}
              sx={{ border: '1px solid #e2e8f0', mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>{mod.moduleName}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {(mod.permissions || []).map((p) => (
                    <FormControlLabel
                      key={p.id}
                      control={
                        <Checkbox
                          checked={selectedIds.has(p.id)}
                          onChange={() => togglePerm(p.id)}
                          size='small'
                        />
                      }
                      label={
                        <Typography variant='caption' title={p.code}>
                          {p.name}
                        </Typography>
                      }
                    />
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant='contained' onClick={save}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
